/**
 * ClientBillingTab - Manages billing settings for a specific client company
 * 
 * Used by consultants to:
 * - Set who pays (consultant or client)
 * - Override self-service settings
 * - View client's trial/credit status
 * 
 * Used by clients (when they pay) to:
 * - View their billing status
 * - Purchase credit packs
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Switch,
  Stack,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  ShoppingCart as ShoppingCartIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Star as StarIcon,
  Rocket as RocketIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { billingAPI, ClientBillingSettings } from '../../services/api';

// Credit pack pricing (same as BillingSettings)
const CREDIT_PACKS = [
  { type: 'starter' as const, name: 'Starter', credits: 3, price: 249, icon: <StarIcon /> },
  { type: 'professional' as const, name: 'Professional', credits: 10, price: 699, icon: <RocketIcon />, popular: true },
  { type: 'scale' as const, name: 'Scale', credits: 30, price: 1499, icon: <BusinessIcon /> },
];

interface ClientBillingTabProps {
  companyId: number;
  companyName: string;
  isConsultantView: boolean;  // true if consultant is viewing, false if client
}

const ClientBillingTab: React.FC<ClientBillingTabProps> = ({ 
  companyId, 
  companyName,
  isConsultantView 
}) => {
  const [billing, setBilling] = useState<ClientBillingSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Load billing data
  useEffect(() => {
    const loadBilling = async () => {
      try {
        setIsLoading(true);
        const data = await billingAPI.getClientBilling(companyId);
        setBilling(data);
      } catch (err: any) {
        console.error('Error loading client billing:', err);
        setError(err.message || 'Failed to load billing data');
      } finally {
        setIsLoading(false);
      }
    };

    loadBilling();
  }, [companyId]);

  // Handle billing responsibility change
  const handleBillingResponsibilityChange = async (value: 'consultant' | 'client') => {
    try {
      setIsProcessing(true);
      setError(null);
      await billingAPI.updateClientBilling(companyId, { billing_responsibility: value });
      setBilling(prev => prev ? { ...prev, billing_responsibility: value } : null);
      setSuccessMessage('Billing responsibility updated');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update billing');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle self-service override change
  const handleSelfServiceChange = async (value: boolean | null) => {
    try {
      setIsProcessing(true);
      setError(null);
      await billingAPI.updateClientBilling(companyId, { allow_self_service: value });
      setBilling(prev => prev ? { ...prev, allow_self_service: value ?? false } : null);
      setSuccessMessage('Self-service setting updated');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update settings');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle credit purchase (for client view)
  const handlePurchaseCredits = async (packType: 'starter' | 'professional' | 'scale') => {
    try {
      setIsProcessing(true);
      setError(null);
      const { checkout_url } = await billingAPI.createCheckoutSession(packType, companyId);
      window.location.href = checkout_url;
    } catch (err: any) {
      setError(err.message || 'Failed to start checkout');
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (!billing) {
    return (
      <Alert severity="error">
        Failed to load billing information.
      </Alert>
    );
  }

  // Consultant view - manage client billing settings
  if (isConsultantView) {
    return (
      <Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}

        {/* Billing Responsibility */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Who Pays for Audits?
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Choose whether you (consultant) or {companyName} pays for audit credits.
            </Typography>
            <Divider sx={{ my: 2 }} />
            
            <FormControl component="fieldset" disabled={isProcessing}>
              <RadioGroup
                value={billing.billing_responsibility}
                onChange={(e) => handleBillingResponsibilityChange(e.target.value as 'consultant' | 'client')}
              >
                <FormControlLabel
                  value="consultant"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">Consultant Pays</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Credits are deducted from your shared pool
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="client"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">Client Pays</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {companyName} purchases their own credits
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          </CardContent>
        </Card>

        {/* Self-Service Settings */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Self-Service Access
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Control whether {companyName} can run their own audits.
            </Typography>
            <Divider sx={{ my: 2 }} />
            
            {billing.billing_responsibility === 'client' ? (
              <Alert severity="info">
                Self-service is automatically enabled when the client pays for their own credits.
              </Alert>
            ) : (
              <FormControlLabel
                control={
                  <Switch
                    checked={billing.allow_self_service}
                    onChange={(e) => handleSelfServiceChange(e.target.checked)}
                    disabled={isProcessing}
                  />
                }
                label="Allow client to run their own audits"
              />
            )}
          </CardContent>
        </Card>

        {/* Client Status */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Client Status
            </Typography>
            <Divider sx={{ my: 2 }} />
            
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, md: 3 }}>
                <Typography variant="body2" color="text.secondary">Billing</Typography>
                <Chip
                  size="small"
                  label={billing.billing_responsibility === 'consultant' ? 'You Pay' : 'Client Pays'}
                  color={billing.billing_responsibility === 'consultant' ? 'primary' : 'secondary'}
                />
              </Grid>
              
              {billing.billing_responsibility === 'client' && (
                <>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Typography variant="body2" color="text.secondary">Credits</Typography>
                    <Typography variant="h6">{billing.audit_credits_balance}</Typography>
                  </Grid>
                  
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Typography variant="body2" color="text.secondary">Trial Status</Typography>
                    <Chip
                      size="small"
                      icon={billing.trial_audit_used ? <CheckCircleIcon /> : <WarningIcon />}
                      label={billing.trial_audit_used ? 'Trial Used' : `${billing.trial_days_remaining} days left`}
                      color={billing.trial_audit_used ? 'default' : 'info'}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Client view - their own billing (only shown if client pays)
  if (billing.billing_responsibility === 'consultant') {
    return (
      <Alert severity="info">
        <Typography variant="body1" gutterBottom>
          Billing Managed by Consultant
        </Typography>
        <Typography variant="body2">
          Your consultant manages billing for your account. Contact them for any billing questions.
        </Typography>
      </Alert>
    );
  }

  // Client pays - show their billing interface
  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Credit Balance */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCartIcon /> Your Audit Credits
          </Typography>
          <Divider sx={{ my: 2 }} />
          
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <Box textAlign="center" p={2} bgcolor="action.hover" borderRadius={2}>
                <Typography variant="h2" color="primary">
                  {billing.audit_credits_balance}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Credits Available
                </Typography>
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, md: 8 }}>
              {!billing.trial_audit_used && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  You have a free trial audit available! {billing.trial_days_remaining} days remaining.
                </Alert>
              )}
              
              <Typography variant="body2" color="text.secondary">
                Purchase credit packs below to run more audits.
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Credit Packs */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Purchase Credit Packs
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Credits expire 2 years from purchase.
          </Typography>
          <Divider sx={{ my: 2 }} />
          
          <Grid container spacing={2}>
            {CREDIT_PACKS.map((pack) => (
              <Grid size={{ xs: 12, sm: 4 }} key={pack.type}>
                <Card 
                  variant="outlined"
                  sx={{ 
                    textAlign: 'center',
                    border: pack.popular ? 2 : 1,
                    borderColor: pack.popular ? 'primary.main' : 'divider',
                  }}
                >
                  <CardContent>
                    <Box color="primary.main">{pack.icon}</Box>
                    <Typography variant="subtitle1">{pack.name}</Typography>
                    <Typography variant="h5" color="primary">${pack.price}</Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {pack.credits} credits
                    </Typography>
                    <Button
                      variant={pack.popular ? 'contained' : 'outlined'}
                      size="small"
                      fullWidth
                      onClick={() => handlePurchaseCredits(pack.type)}
                      disabled={isProcessing}
                    >
                      Buy
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ClientBillingTab;

